/**
 * Icon mapping utility for converting protobuf Icon enum values to bsky-icons components
 */

import { Icons } from "@storytell/cp-client";
import type { IconTypes } from "solid-icons";
import {
  TbAlertCircle,
  TbAlertTriangle,
  TbAt,
  TbAtom,
  TbBell,
  TbBellOff,
  TbBellRinging,
  TbBellZ,
  TbBolt,
  TbBook,
  TbBookmark,
  TbBooks,
  TbBriefcase,
  TbBuilding,
  TbBulb,
  TbCheck,
  TbChevronDown,
  TbChevronLeft,
  TbChevronRight,
  TbChevronUp,
  TbClipboard,
  TbClock,
  TbCoin,
  TbCrown,
  TbCurrencyDollar,
  TbDots,
  TbEye,
  TbFile,
  TbFileExport,
  TbFileStack,
  TbFileText,
  TbFolder,
  TbGraph,
  TbHash,
  TbHeadset,
  TbHeart,
  TbHierarchy,
  TbInfoCircle,
  TbLayoutGrid,
  TbLifebuoy,
  TbLink,
  TbList,
  TbLock,
  TbLockOpen,
  TbMail,
  TbMapPin,
  TbMaximize,
  TbMessage2,
  TbMessages,
  TbQuestionMark,
  TbSelector,
  TbSpeakerphone,
  TbUser,
} from "solid-icons/tb";
import type { Component } from "solid-js";
import AnalysisIcon from "./AnalysisIcon";
import AnalystIcon from "./AnalystIcon";
import ApprovalIcon from "./ApprovalIcon";
import ArrowsInIcon from "./ArrowsInIcon";
import AuditIcon from "./AuditIcon";
import AutomationIcon from "./AutomationIcon";
import BackupIcon from "./BackupIcon";
import BestPracticesIcon from "./BestPracticesIcon";
import BrainstormingIcon from "./BrainstormingIcon";
import BudgetingIcon from "./BudgetingIcon";
import CampaignIcon from "./CampaignIcon";
import CodingIcon from "./CodingIcon";
import CollaborationIcon from "./CollaborationIcon";
import CommunicationIcon from "./CommunicationIcon";
import ComparisonIcon from "./ComparisonIcon";
import ComplianceIcon from "./ComplianceIcon";
import ConsultingIcon from "./ConsultingIcon";
import ContentCreationIcon from "./ContentCreationIcon";
import ContractIcon from "./ContractIcon";
import CreationIcon from "./CreationIcon";
import CustomerServiceIcon from "./CustomerServiceIcon";
import CustomerSuccessIcon from "./CustomerSuccessIcon";
import DataVisualizationIcon from "./DataVisualizationIcon";
import DeploymentIcon from "./DeploymentIcon";
import DesignIcon from "./DesignIcon";
import DiagnosisIcon from "./DiagnosisIcon";
import EmailIcon from "./EmailIcon";
import EngineeringIcon from "./EngineeringIcon";
import ExampleIcon from "./ExampleIcon";
import FeatureExplanationIcon from "./FeatureExplanationIcon";
import FeedbackIcon from "./FeedbackIcon";
import FinanceIcon from "./FinanceIcon";
import ForecastingIcon from "./ForecastingIcon";
import GiftIcon from "./GiftIcon";
import HowToIcon from "./HowToIcon";
import HumanResourcesIcon from "./HumanResourcesIcon";
import HybridKnowledgeIcon from "./HybridKnowledgeIcon";
import IntegrationIcon from "./IntegrationIcon";
import InterviewIcon from "./InterviewIcon";
import InventoryIcon from "./InventoryIcon";
import InvestigationIcon from "./InvestigationIcon";
import InvestmentIcon from "./InvestmentIcon";
import ITIcon from "./ITIcon";
import LegalIcon from "./LegalIcon";
import MaintenanceIcon from "./MaintenanceIcon";
import MarketingIcon from "./MarketingIcon";
import MeetingIcon from "./MeetingIcon";
import MigrationIcon from "./MigrationIcon";
import MinimalNavIcon from "./MinimalNavIcon";
import MonitoringIcon from "./MonitoringIcon";
import NavigateIcon from "./NavigateIcon";
import NegotiationIcon from "./NegotiationIcon";
import NetworkingIcon from "./NetworkingIcon";
import NextIcon from "./NextIcon";
import OnboardingIcon from "./OnboardingIcon";
import OperationsIcon from "./OperationsIcon";
import OptimizationIcon from "./OptimizationIcon";
import PaperclipIcon from "./PaperclipIcon";
import PauseIcon from "./PauseIcon";
import PencilSquareIcon from "./PencilSquareIcon";
import PlanningIcon from "./PlanningIcon";
import PlayIcon from "./PlayIcon";
import PlusIcon from "./PlusIcon";
import PresentationIcon from "./PresentationIcon";
import PreviousIcon from "./PreviousIcon";
import ProcessIcon from "./ProcessIcon";
import ProductManagementIcon from "./ProductManagementIcon";
import ProductUsageIcon from "./ProductUsageIcon";
import ProjectManagementIcon from "./ProjectManagementIcon";
import PromptIcon from "./PromptIcon";
import PrototypingIcon from "./PrototypingIcon";
import QuestionMarkIcon from "./QuestionMarkIcon";
import RecoveryIcon from "./RecoveryIcon";
import RecruitmentIcon from "./RecruitmentIcon";
import RedoIcon from "./RedoIcon";
import ReportingIcon from "./ReportingIcon";
import ResearchIcon from "./ResearchIcon";
import ReviewIcon from "./ReviewIcon";
import RoadmapIcon from "./RoadmapIcon";
import SalesIcon from "./SalesIcon";
import SchedulingIcon from "./SchedulingIcon";
import SearchIcon from "./SearchIcon";
import SecurityIcon from "./SecurityIcon";
import SettingsIcon from "./SettingsIcon";
import ShareIcon from "./ShareIcon";
import ShortcutIcon from "./ShortcutIcon";
import SlashInBoxIcon from "./SlashInBoxIcon";
import SparklesIcon from "./SparklesIcon";
import StarIcon from "./StarIcon";
import StopIcon from "./StopIcon";
import StrategyIcon from "./StrategyIcon";
import SupportIcon from "./SupportIcon";
import SyncIcon from "./SyncIcon";
import SynthesisIcon from "./SynthesisIcon";
import TaskIcon from "./TaskIcon";
import TemplateIcon from "./TemplateIcon";
import TestingIcon from "./TestingIcon";
import TildeIcon from "./TildeIcon";
import TrainingIcon from "./TrainingIcon";
import TransformationIcon from "./TransformationIcon";
import TrashIcon from "./TrashIcon";
import TroubleshootingIcon from "./TroubleshootingIcon";
import TutorialIcon from "./TutorialIcon";
import UndoIcon from "./UndoIcon";
import UserExperienceIcon from "./UserExperienceIcon";
import UserIcon from "./UserIcon";
import VendorIcon from "./VendorIcon";
import WarningIcon from "./WarningIcon";
import WorkflowGuidanceIcon from "./WorkflowGuidanceIcon";
import WorkflowIcon from "./WorkflowIcon";
import WorkspacesIcon from "./WorkspacesIcon";
import WorldIcon from "./WorldIcon";
import WorldKnowledgeIcon from "./WorldKnowledgeIcon";
import WriteIcon from "./WriteIcon";
import XMarkIcon from "./XMarkIcon";

export type IconComponent = Component<{ class?: string; size?: number }>;

/**
 * Too many icons were added
 *
 * This is an effort to sanction a more limited palette
 * for the users to be able to pick from (e.g. for labels)
 */
export const SANCTIONED_ICONS = [
  // Original icons (keeping all existing ones)
  Icons.Icon.AGENT,
  Icons.Icon.ARROWS_IN,
  Icons.Icon.AT_SYMBOL,
  Icons.Icon.AVATAR,
  Icons.Icon.BELL,
  Icons.Icon.BELL_ALERT,
  Icons.Icon.BELL_SLASH,
  Icons.Icon.BELL_SNOOZE,
  Icons.Icon.BOARD,
  Icons.Icon.BOOKMARK,
  Icons.Icon.BUILDING,
  Icons.Icon.BULLHORN,
  Icons.Icon.CHART,
  Icons.Icon.CHAT,
  Icons.Icon.CHATS,
  Icons.Icon.CHECK,
  Icons.Icon.CHEVRON_DOWN,
  Icons.Icon.CHEVRON_LEFT,
  Icons.Icon.CHEVRON_RIGHT,
  Icons.Icon.CHEVRON_UP,
  Icons.Icon.CHEVRON_UP_DOWN,
  Icons.Icon.CLIPBOARD,
  Icons.Icon.CLOCK,
  Icons.Icon.DOCUMENT,
  Icons.Icon.DOCUMENT_STACK,
  Icons.Icon.DOCUMENTATION,
  Icons.Icon.DOT_SEPARATOR,
  Icons.Icon.ENVELOPE,
  Icons.Icon.EXCLAMATION_CIRCLE,
  Icons.Icon.EXCLAMATION_TRIANGLE,
  Icons.Icon.EXPAND,
  Icons.Icon.EXPORT,
  Icons.Icon.EYE,
  Icons.Icon.FILE,
  Icons.Icon.FOLDER,
  Icons.Icon.HASH,
  Icons.Icon.HYBRID_KNOWLEDGE,
  Icons.Icon.INFORMATION,
  Icons.Icon.INTEGRATION,
  Icons.Icon.INTERNAL_KNOWLEDGE,
  Icons.Icon.JOBS,
  Icons.Icon.LIBRARY,
  Icons.Icon.LIGHT_BULB,
  Icons.Icon.LIGHTNING,
  Icons.Icon.LIST,
  Icons.Icon.LOCK,
  Icons.Icon.LOCK_CLOSED,
  Icons.Icon.MAP_PIN,

  // Additional verified Tabler icons (safely adding more)
  Icons.Icon.MINIMAL_NAV,
  Icons.Icon.NAVIGATE,
  Icons.Icon.NEXT,
  Icons.Icon.PAPERCLIP,
  Icons.Icon.PAUSE,
  Icons.Icon.PLAY,
  Icons.Icon.PLUS,
  Icons.Icon.PREVIOUS,
  Icons.Icon.PROMPT,
  Icons.Icon.QUESTION_MARK,
  Icons.Icon.REDO,
  Icons.Icon.SEARCH,
  Icons.Icon.SETTINGS,
  Icons.Icon.SHARE,
  Icons.Icon.SLASH_IN_BOX,
  Icons.Icon.SPARKLES,
  Icons.Icon.STAR,
  Icons.Icon.STOP,
  Icons.Icon.SYNC,
  Icons.Icon.TILDE,
  Icons.Icon.TRASH,
  Icons.Icon.UNDO,
  Icons.Icon.USER,
  Icons.Icon.WARNING,
  Icons.Icon.WORKFLOW,
  Icons.Icon.WORKSPACES,
  Icons.Icon.WORLD,
  Icons.Icon.WORLD_KNOWLEDGE,
  Icons.Icon.X_MARK,
  Icons.Icon.FEEDBACK,
  Icons.Icon.GIFT,

  // Additional existing enum values (100+ more icons)
  Icons.Icon.ANALYSIS,
  Icons.Icon.COMPARISON,
  Icons.Icon.INVESTIGATION,
  Icons.Icon.AUDIT,
  Icons.Icon.REPORTING,
  Icons.Icon.MONITORING,
  Icons.Icon.FORECASTING,
  Icons.Icon.DIAGNOSIS,
  Icons.Icon.PRODUCT_USAGE,
  Icons.Icon.FEATURE_EXPLANATION,
  Icons.Icon.HOW_TO,
  Icons.Icon.BEST_PRACTICES,
  Icons.Icon.TUTORIAL,
  Icons.Icon.EXAMPLE,
  Icons.Icon.WORKFLOW_GUIDANCE,
  Icons.Icon.SHORTCUT,
  Icons.Icon.CREATION,
  Icons.Icon.TRANSFORMATION,
  Icons.Icon.SYNTHESIS,
  Icons.Icon.OPTIMIZATION,
  Icons.Icon.DESIGN,
  Icons.Icon.PROTOTYPING,
  Icons.Icon.TEMPLATE,
  Icons.Icon.TASK,
  Icons.Icon.PROCESS,
  Icons.Icon.AUTOMATION,
  Icons.Icon.DEPLOYMENT,
  Icons.Icon.MIGRATION,
  Icons.Icon.BACKUP,
  Icons.Icon.RECOVERY,
  Icons.Icon.SALES,
  Icons.Icon.MARKETING,
  Icons.Icon.CUSTOMER_SUCCESS,
  Icons.Icon.ANALYST,
  Icons.Icon.FINANCE,
  Icons.Icon.ENGINEERING,
  Icons.Icon.HUMAN_RESOURCES,
  Icons.Icon.PRODUCT_MANAGEMENT,
  Icons.Icon.OPERATIONS,
  Icons.Icon.IT,
  Icons.Icon.LEGAL,
  Icons.Icon.STRATEGY,
  Icons.Icon.RESEARCH,
  Icons.Icon.SECURITY,
  Icons.Icon.COMPLIANCE,
  Icons.Icon.BRAINSTORMING,
  Icons.Icon.MEETING,
  Icons.Icon.CODING,
  Icons.Icon.DATA_VISUALIZATION,
  Icons.Icon.CONTRACT,
  Icons.Icon.RECRUITMENT,
  Icons.Icon.BUDGETING,
  Icons.Icon.CAMPAIGN,
  Icons.Icon.PROJECT_MANAGEMENT,
  Icons.Icon.TESTING,
  Icons.Icon.WRITE,
  Icons.Icon.PLANNING,
  Icons.Icon.SCHEDULING,
  Icons.Icon.EMAIL,
  Icons.Icon.INTERVIEW,
  Icons.Icon.NEGOTIATION,
  Icons.Icon.NETWORKING,
  Icons.Icon.ONBOARDING,
  Icons.Icon.ROADMAP,
  Icons.Icon.USER_EXPERIENCE,
  Icons.Icon.INVENTORY,
  Icons.Icon.VENDOR,
  Icons.Icon.CUSTOMER_SERVICE,
  Icons.Icon.CONTENT_CREATION,
  Icons.Icon.INVESTMENT,
  Icons.Icon.COLLABORATION,
  Icons.Icon.COMMUNICATION,
  Icons.Icon.REVIEW,
  Icons.Icon.APPROVAL,
  Icons.Icon.PRESENTATION,
  Icons.Icon.TRAINING,
  Icons.Icon.SUPPORT,
  Icons.Icon.TROUBLESHOOTING,
  Icons.Icon.MAINTENANCE,
  Icons.Icon.CONSULTING,
  Icons.Icon.PENCIL_SQUARE,
  Icons.Icon.HIERARCHY,
  Icons.Icon.HEART,
  Icons.Icon.CROWN,
  Icons.Icon.LIFELINE,
  Icons.Icon.COIN,
  Icons.Icon.CURRENCY,
  Icons.Icon.HEADSET,
  Icons.Icon.LINK,
  Icons.Icon.BOOKS,
];

/**
 * Maps protobuf Icon enum values to their corresponding bsky-icons components
 */
export const ICON_MAPPING: Record<Icons.Icon, IconTypes | IconComponent> = {
  [Icons.Icon.UNSPECIFIED]: TbQuestionMark,

  // Basic UI icons
  [Icons.Icon.AGENT]: TbAtom,
  [Icons.Icon.ARROWS_IN]: ArrowsInIcon,
  [Icons.Icon.AT_SYMBOL]: TbAt,
  [Icons.Icon.AVATAR]: TbUser,
  [Icons.Icon.BELL]: TbBell,
  [Icons.Icon.BELL_ALERT]: TbBellRinging,
  [Icons.Icon.BELL_SLASH]: TbBellOff,
  [Icons.Icon.BELL_SNOOZE]: TbBellZ,
  [Icons.Icon.BOARD]: TbLayoutGrid,
  [Icons.Icon.BOOKMARK]: TbBookmark,
  [Icons.Icon.BUILDING]: TbBuilding,
  [Icons.Icon.BULLHORN]: TbSpeakerphone,
  [Icons.Icon.CHART]: TbGraph,
  [Icons.Icon.CHAT]: TbMessage2,
  [Icons.Icon.CHATS]: TbMessages,
  [Icons.Icon.CHECK]: TbCheck,
  [Icons.Icon.CHEVRON_DOWN]: TbChevronDown,
  [Icons.Icon.CHEVRON_LEFT]: TbChevronLeft,
  [Icons.Icon.CHEVRON_RIGHT]: TbChevronRight,
  [Icons.Icon.CHEVRON_UP]: TbChevronUp,
  [Icons.Icon.CHEVRON_UP_DOWN]: TbSelector,
  [Icons.Icon.CLIPBOARD]: TbClipboard,
  [Icons.Icon.CLOCK]: TbClock,
  [Icons.Icon.DOCUMENT]: TbFileText,
  [Icons.Icon.DOCUMENT_STACK]: TbFileStack,
  [Icons.Icon.DOCUMENTATION]: TbFileText,
  [Icons.Icon.DOT_SEPARATOR]: TbDots,
  [Icons.Icon.ENVELOPE]: TbMail,
  [Icons.Icon.EXCLAMATION_CIRCLE]: TbAlertCircle,
  [Icons.Icon.EXCLAMATION_TRIANGLE]: TbAlertTriangle,
  [Icons.Icon.EXPAND]: TbMaximize,
  [Icons.Icon.EXPORT]: TbFileExport,
  [Icons.Icon.EYE]: TbEye,
  [Icons.Icon.FILE]: TbFile,
  [Icons.Icon.FOLDER]: TbFolder,
  [Icons.Icon.HASH]: TbHash,
  [Icons.Icon.HYBRID_KNOWLEDGE]: HybridKnowledgeIcon,
  [Icons.Icon.INFORMATION]: TbInfoCircle,
  [Icons.Icon.INTEGRATION]: IntegrationIcon,
  [Icons.Icon.INTERNAL_KNOWLEDGE]: TbBook,
  [Icons.Icon.JOBS]: TbBriefcase,
  [Icons.Icon.LIBRARY]: TbBook,
  [Icons.Icon.LIGHT_BULB]: TbBulb,
  [Icons.Icon.LIGHTNING]: TbBolt,
  [Icons.Icon.LIST]: TbList,
  [Icons.Icon.LOCK]: TbLockOpen,
  [Icons.Icon.LOCK_CLOSED]: TbLock,
  [Icons.Icon.MAP_PIN]: TbMapPin,
  [Icons.Icon.MINIMAL_NAV]: MinimalNavIcon,
  [Icons.Icon.NAVIGATE]: NavigateIcon,
  [Icons.Icon.NEXT]: NextIcon,
  [Icons.Icon.PAPERCLIP]: PaperclipIcon,
  [Icons.Icon.PAUSE]: PauseIcon,
  [Icons.Icon.PLAY]: PlayIcon,
  [Icons.Icon.PLUS]: PlusIcon,
  [Icons.Icon.PREVIOUS]: PreviousIcon,
  [Icons.Icon.PROMPT]: PromptIcon,
  [Icons.Icon.QUESTION_MARK]: QuestionMarkIcon,
  [Icons.Icon.REDO]: RedoIcon,
  [Icons.Icon.SEARCH]: SearchIcon,
  [Icons.Icon.SETTINGS]: SettingsIcon,
  [Icons.Icon.SHARE]: ShareIcon,
  [Icons.Icon.SLASH_IN_BOX]: SlashInBoxIcon,
  [Icons.Icon.SPARKLES]: SparklesIcon,
  [Icons.Icon.STAR]: StarIcon,
  [Icons.Icon.STOP]: StopIcon,
  [Icons.Icon.SYNC]: SyncIcon,
  [Icons.Icon.TILDE]: TildeIcon,
  [Icons.Icon.TRASH]: TrashIcon,
  [Icons.Icon.UNDO]: UndoIcon,
  [Icons.Icon.USER]: UserIcon,
  [Icons.Icon.WARNING]: WarningIcon,
  [Icons.Icon.WORKFLOW]: WorkflowIcon,
  [Icons.Icon.WORKSPACES]: WorkspacesIcon,
  [Icons.Icon.WORLD]: WorldIcon,
  [Icons.Icon.WORLD_KNOWLEDGE]: WorldKnowledgeIcon,
  [Icons.Icon.X_MARK]: XMarkIcon,
  [Icons.Icon.FEEDBACK]: FeedbackIcon,
  [Icons.Icon.GIFT]: GiftIcon,

  // Intent-based icons
  [Icons.Icon.ANALYSIS]: AnalysisIcon,
  [Icons.Icon.COMPARISON]: ComparisonIcon,
  [Icons.Icon.INVESTIGATION]: InvestigationIcon,
  [Icons.Icon.AUDIT]: AuditIcon,
  [Icons.Icon.REPORTING]: ReportingIcon,
  [Icons.Icon.MONITORING]: MonitoringIcon,
  [Icons.Icon.FORECASTING]: ForecastingIcon,
  [Icons.Icon.DIAGNOSIS]: DiagnosisIcon,
  [Icons.Icon.PRODUCT_USAGE]: ProductUsageIcon,
  [Icons.Icon.FEATURE_EXPLANATION]: FeatureExplanationIcon,
  [Icons.Icon.HOW_TO]: HowToIcon,
  [Icons.Icon.BEST_PRACTICES]: BestPracticesIcon,
  [Icons.Icon.TUTORIAL]: TutorialIcon,
  [Icons.Icon.EXAMPLE]: ExampleIcon,
  [Icons.Icon.WORKFLOW_GUIDANCE]: WorkflowGuidanceIcon,
  [Icons.Icon.SHORTCUT]: ShortcutIcon,
  [Icons.Icon.CREATION]: CreationIcon,
  [Icons.Icon.TRANSFORMATION]: TransformationIcon,
  [Icons.Icon.SYNTHESIS]: SynthesisIcon,
  [Icons.Icon.OPTIMIZATION]: OptimizationIcon,
  [Icons.Icon.DESIGN]: DesignIcon,
  [Icons.Icon.PROTOTYPING]: PrototypingIcon,
  [Icons.Icon.TEMPLATE]: TemplateIcon,
  [Icons.Icon.TASK]: TaskIcon,
  [Icons.Icon.PROCESS]: ProcessIcon,
  [Icons.Icon.AUTOMATION]: AutomationIcon,
  [Icons.Icon.DEPLOYMENT]: DeploymentIcon,
  [Icons.Icon.MIGRATION]: MigrationIcon,
  [Icons.Icon.BACKUP]: BackupIcon,
  [Icons.Icon.RECOVERY]: RecoveryIcon,

  // Domain-based icons
  [Icons.Icon.SALES]: SalesIcon,
  [Icons.Icon.MARKETING]: MarketingIcon,
  [Icons.Icon.CUSTOMER_SUCCESS]: CustomerSuccessIcon,
  [Icons.Icon.ANALYST]: AnalystIcon,
  [Icons.Icon.FINANCE]: FinanceIcon,
  [Icons.Icon.ENGINEERING]: EngineeringIcon,
  [Icons.Icon.HUMAN_RESOURCES]: HumanResourcesIcon,
  [Icons.Icon.PRODUCT_MANAGEMENT]: ProductManagementIcon,
  [Icons.Icon.OPERATIONS]: OperationsIcon,
  [Icons.Icon.IT]: ITIcon,
  [Icons.Icon.LEGAL]: LegalIcon,
  [Icons.Icon.STRATEGY]: StrategyIcon,
  [Icons.Icon.RESEARCH]: ResearchIcon,
  [Icons.Icon.SECURITY]: SecurityIcon,
  [Icons.Icon.COMPLIANCE]: ComplianceIcon,

  // Communication and collaboration icons
  [Icons.Icon.COLLABORATION]: CollaborationIcon,
  [Icons.Icon.COMMUNICATION]: CommunicationIcon,
  [Icons.Icon.REVIEW]: ReviewIcon,
  [Icons.Icon.APPROVAL]: ApprovalIcon,
  [Icons.Icon.PRESENTATION]: PresentationIcon,
  [Icons.Icon.TRAINING]: TrainingIcon,
  [Icons.Icon.SUPPORT]: SupportIcon,
  [Icons.Icon.TROUBLESHOOTING]: TroubleshootingIcon,
  [Icons.Icon.MAINTENANCE]: MaintenanceIcon,

  // Work activity icons
  [Icons.Icon.BRAINSTORMING]: BrainstormingIcon,
  [Icons.Icon.MEETING]: MeetingIcon,
  [Icons.Icon.CODING]: CodingIcon,
  [Icons.Icon.DATA_VISUALIZATION]: DataVisualizationIcon,
  [Icons.Icon.CONTRACT]: ContractIcon,
  [Icons.Icon.RECRUITMENT]: RecruitmentIcon,
  [Icons.Icon.BUDGETING]: BudgetingIcon,
  [Icons.Icon.CAMPAIGN]: CampaignIcon,
  [Icons.Icon.PROJECT_MANAGEMENT]: ProjectManagementIcon,
  [Icons.Icon.TESTING]: TestingIcon,
  [Icons.Icon.WRITE]: WriteIcon,
  [Icons.Icon.PLANNING]: PlanningIcon,
  [Icons.Icon.SCHEDULING]: SchedulingIcon,
  [Icons.Icon.EMAIL]: EmailIcon,
  [Icons.Icon.INTERVIEW]: InterviewIcon,
  [Icons.Icon.NEGOTIATION]: NegotiationIcon,
  [Icons.Icon.NETWORKING]: NetworkingIcon,
  [Icons.Icon.ONBOARDING]: OnboardingIcon,
  [Icons.Icon.ROADMAP]: RoadmapIcon,
  [Icons.Icon.USER_EXPERIENCE]: UserExperienceIcon,
  [Icons.Icon.INVENTORY]: InventoryIcon,
  [Icons.Icon.VENDOR]: VendorIcon,
  [Icons.Icon.CUSTOMER_SERVICE]: CustomerServiceIcon,
  [Icons.Icon.CONTENT_CREATION]: ContentCreationIcon,
  [Icons.Icon.INVESTMENT]: InvestmentIcon,
  [Icons.Icon.CONSULTING]: ConsultingIcon,
  [Icons.Icon.PENCIL_SQUARE]: PencilSquareIcon,

  // Persona / general-purpose icons
  [Icons.Icon.HIERARCHY]: TbHierarchy,
  [Icons.Icon.HEART]: TbHeart,
  [Icons.Icon.CROWN]: TbCrown,
  [Icons.Icon.LIFELINE]: TbLifebuoy,
  [Icons.Icon.COIN]: TbCoin,
  [Icons.Icon.CURRENCY]: TbCurrencyDollar,
  [Icons.Icon.HEADSET]: TbHeadset,
  [Icons.Icon.LINK]: TbLink,
  [Icons.Icon.BOOKS]: TbBooks,
};

export const iconEnumToString = (icon: Icons.Icon) => {
  const value = Object.entries(Icons.Icon).find(([_, iconValue]) => iconValue === icon)?.[0] || "UNSPECIFIED";
  return `ICON_${value}`;
};
/**
 * String to Icon enum mapping for backwards compatibility with string icon values
 */
const STRING_TO_ICON_MAPPING: Record<string, Icons.Icon> = {
  // Basic UI icons
  ICON_UNSPECIFIED: Icons.Icon.UNSPECIFIED,
  ICON_AGENT: Icons.Icon.AGENT,
  ICON_ARROWS_IN: Icons.Icon.ARROWS_IN,
  ICON_AT_SYMBOL: Icons.Icon.AT_SYMBOL,
  ICON_AVATAR: Icons.Icon.AVATAR,
  ICON_BELL: Icons.Icon.BELL,
  ICON_BELL_ALERT: Icons.Icon.BELL_ALERT,
  ICON_BELL_SLASH: Icons.Icon.BELL_SLASH,
  ICON_BELL_SNOOZE: Icons.Icon.BELL_SNOOZE,
  ICON_BOARD: Icons.Icon.BOARD,
  ICON_BOOKMARK: Icons.Icon.BOOKMARK,
  ICON_BUILDING: Icons.Icon.BUILDING,
  ICON_BULLHORN: Icons.Icon.BULLHORN,
  ICON_CHART: Icons.Icon.CHART,
  ICON_CHAT: Icons.Icon.CHAT,
  ICON_CHATS: Icons.Icon.CHATS,
  ICON_CHECK: Icons.Icon.CHECK,
  ICON_CHEVRON_DOWN: Icons.Icon.CHEVRON_DOWN,
  ICON_CHEVRON_LEFT: Icons.Icon.CHEVRON_LEFT,
  ICON_CHEVRON_RIGHT: Icons.Icon.CHEVRON_RIGHT,
  ICON_CHEVRON_UP: Icons.Icon.CHEVRON_UP,
  ICON_CHEVRON_UP_DOWN: Icons.Icon.CHEVRON_UP_DOWN,
  ICON_CLIPBOARD: Icons.Icon.CLIPBOARD,
  ICON_CLOCK: Icons.Icon.CLOCK,
  ICON_DOCUMENT: Icons.Icon.DOCUMENT,
  ICON_DOCUMENT_STACK: Icons.Icon.DOCUMENT_STACK,
  ICON_DOCUMENTATION: Icons.Icon.DOCUMENTATION,
  ICON_DOT_SEPARATOR: Icons.Icon.DOT_SEPARATOR,
  ICON_ENVELOPE: Icons.Icon.ENVELOPE,
  ICON_EXCLAMATION_CIRCLE: Icons.Icon.EXCLAMATION_CIRCLE,
  ICON_EXCLAMATION_TRIANGLE: Icons.Icon.EXCLAMATION_TRIANGLE,
  ICON_EXPAND: Icons.Icon.EXPAND,
  ICON_EXPORT: Icons.Icon.EXPORT,
  ICON_EYE: Icons.Icon.EYE,
  ICON_FILE: Icons.Icon.FILE,
  ICON_FOLDER: Icons.Icon.FOLDER,
  ICON_HASH: Icons.Icon.HASH,
  ICON_HYBRID_KNOWLEDGE: Icons.Icon.HYBRID_KNOWLEDGE,
  ICON_INFORMATION: Icons.Icon.INFORMATION,
  ICON_INTEGRATION: Icons.Icon.INTEGRATION,
  ICON_INTERNAL_KNOWLEDGE: Icons.Icon.INTERNAL_KNOWLEDGE,
  ICON_JOBS: Icons.Icon.JOBS,
  ICON_LIBRARY: Icons.Icon.LIBRARY,
  ICON_LIGHT_BULB: Icons.Icon.LIGHT_BULB,
  ICON_LIGHTNING: Icons.Icon.LIGHTNING,
  ICON_LIST: Icons.Icon.LIST,
  ICON_LOCK: Icons.Icon.LOCK,
  ICON_LOCK_CLOSED: Icons.Icon.LOCK_CLOSED,
  ICON_MAP_PIN: Icons.Icon.MAP_PIN,
  ICON_MINIMAL_NAV: Icons.Icon.MINIMAL_NAV,
  ICON_NAVIGATE: Icons.Icon.NAVIGATE,
  ICON_NEXT: Icons.Icon.NEXT,
  ICON_PAPERCLIP: Icons.Icon.PAPERCLIP,
  ICON_PAUSE: Icons.Icon.PAUSE,
  ICON_PLAY: Icons.Icon.PLAY,
  ICON_PLUS: Icons.Icon.PLUS,
  ICON_PREVIOUS: Icons.Icon.PREVIOUS,
  ICON_PROMPT: Icons.Icon.PROMPT,
  ICON_QUESTION_MARK: Icons.Icon.QUESTION_MARK,
  ICON_REDO: Icons.Icon.REDO,
  ICON_SEARCH: Icons.Icon.SEARCH,
  ICON_SETTINGS: Icons.Icon.SETTINGS,
  ICON_SHARE: Icons.Icon.SHARE,
  ICON_SLASH_IN_BOX: Icons.Icon.SLASH_IN_BOX,
  ICON_SPARKLES: Icons.Icon.SPARKLES,
  ICON_STAR: Icons.Icon.STAR,
  ICON_STOP: Icons.Icon.STOP,
  ICON_SYNC: Icons.Icon.SYNC,
  ICON_TILDE: Icons.Icon.TILDE,
  ICON_TRASH: Icons.Icon.TRASH,
  ICON_UNDO: Icons.Icon.UNDO,
  ICON_USER: Icons.Icon.USER,
  ICON_WARNING: Icons.Icon.WARNING,
  ICON_WORKFLOW: Icons.Icon.WORKFLOW,
  ICON_WORKSPACES: Icons.Icon.WORKSPACES,
  ICON_WORLD: Icons.Icon.WORLD,
  ICON_WORLD_KNOWLEDGE: Icons.Icon.WORLD_KNOWLEDGE,
  ICON_X_MARK: Icons.Icon.X_MARK,
  ICON_FEEDBACK: Icons.Icon.FEEDBACK,
  ICON_GIFT: Icons.Icon.GIFT,

  // Intent-based icons
  ICON_ANALYSIS: Icons.Icon.ANALYSIS,
  ICON_COMPARISON: Icons.Icon.COMPARISON,
  ICON_INVESTIGATION: Icons.Icon.INVESTIGATION,
  ICON_AUDIT: Icons.Icon.AUDIT,
  ICON_REPORTING: Icons.Icon.REPORTING,
  ICON_MONITORING: Icons.Icon.MONITORING,
  ICON_FORECASTING: Icons.Icon.FORECASTING,
  ICON_DIAGNOSIS: Icons.Icon.DIAGNOSIS,
  ICON_PRODUCT_USAGE: Icons.Icon.PRODUCT_USAGE,
  ICON_FEATURE_EXPLANATION: Icons.Icon.FEATURE_EXPLANATION,
  ICON_HOW_TO: Icons.Icon.HOW_TO,
  ICON_BEST_PRACTICES: Icons.Icon.BEST_PRACTICES,
  ICON_TUTORIAL: Icons.Icon.TUTORIAL,
  ICON_EXAMPLE: Icons.Icon.EXAMPLE,
  ICON_WORKFLOW_GUIDANCE: Icons.Icon.WORKFLOW_GUIDANCE,
  ICON_SHORTCUT: Icons.Icon.SHORTCUT,
  ICON_CREATION: Icons.Icon.CREATION,
  ICON_TRANSFORMATION: Icons.Icon.TRANSFORMATION,
  ICON_SYNTHESIS: Icons.Icon.SYNTHESIS,
  ICON_OPTIMIZATION: Icons.Icon.OPTIMIZATION,
  ICON_DESIGN: Icons.Icon.DESIGN,
  ICON_PROTOTYPING: Icons.Icon.PROTOTYPING,
  ICON_TEMPLATE: Icons.Icon.TEMPLATE,
  ICON_TASK: Icons.Icon.TASK,
  ICON_PROCESS: Icons.Icon.PROCESS,
  ICON_AUTOMATION: Icons.Icon.AUTOMATION,
  ICON_DEPLOYMENT: Icons.Icon.DEPLOYMENT,
  ICON_MIGRATION: Icons.Icon.MIGRATION,
  ICON_BACKUP: Icons.Icon.BACKUP,
  ICON_RECOVERY: Icons.Icon.RECOVERY,

  // Domain-based icons
  ICON_SALES: Icons.Icon.SALES,
  ICON_MARKETING: Icons.Icon.MARKETING,
  ICON_CUSTOMER_SUCCESS: Icons.Icon.CUSTOMER_SUCCESS,
  ICON_ANALYST: Icons.Icon.ANALYST,
  ICON_FINANCE: Icons.Icon.FINANCE,
  ICON_ENGINEERING: Icons.Icon.ENGINEERING,
  ICON_HUMAN_RESOURCES: Icons.Icon.HUMAN_RESOURCES,
  ICON_PRODUCT_MANAGEMENT: Icons.Icon.PRODUCT_MANAGEMENT,
  ICON_OPERATIONS: Icons.Icon.OPERATIONS,
  ICON_IT: Icons.Icon.IT,
  ICON_LEGAL: Icons.Icon.LEGAL,
  ICON_STRATEGY: Icons.Icon.STRATEGY,
  ICON_RESEARCH: Icons.Icon.RESEARCH,
  ICON_SECURITY: Icons.Icon.SECURITY,
  ICON_COMPLIANCE: Icons.Icon.COMPLIANCE,

  // Communication and collaboration icons
  ICON_COLLABORATION: Icons.Icon.COLLABORATION,
  ICON_COMMUNICATION: Icons.Icon.COMMUNICATION,
  ICON_REVIEW: Icons.Icon.REVIEW,
  ICON_APPROVAL: Icons.Icon.APPROVAL,
  ICON_PRESENTATION: Icons.Icon.PRESENTATION,
  ICON_TRAINING: Icons.Icon.TRAINING,
  ICON_SUPPORT: Icons.Icon.SUPPORT,
  ICON_TROUBLESHOOTING: Icons.Icon.TROUBLESHOOTING,
  ICON_MAINTENANCE: Icons.Icon.MAINTENANCE,

  // Work activity icons
  ICON_BRAINSTORMING: Icons.Icon.BRAINSTORMING,
  ICON_MEETING: Icons.Icon.MEETING,
  ICON_CODING: Icons.Icon.CODING,
  ICON_DATA_VISUALIZATION: Icons.Icon.DATA_VISUALIZATION,
  ICON_CONTRACT: Icons.Icon.CONTRACT,
  ICON_RECRUITMENT: Icons.Icon.RECRUITMENT,
  ICON_BUDGETING: Icons.Icon.BUDGETING,
  ICON_CAMPAIGN: Icons.Icon.CAMPAIGN,
  ICON_PROJECT_MANAGEMENT: Icons.Icon.PROJECT_MANAGEMENT,
  ICON_TESTING: Icons.Icon.TESTING,
  ICON_WRITE: Icons.Icon.WRITE,
  ICON_PLANNING: Icons.Icon.PLANNING,
  ICON_SCHEDULING: Icons.Icon.SCHEDULING,
  ICON_EMAIL: Icons.Icon.EMAIL,
  ICON_INTERVIEW: Icons.Icon.INTERVIEW,
  ICON_NEGOTIATION: Icons.Icon.NEGOTIATION,
  ICON_NETWORKING: Icons.Icon.NETWORKING,
  ICON_ONBOARDING: Icons.Icon.ONBOARDING,
  ICON_ROADMAP: Icons.Icon.ROADMAP,
  ICON_USER_EXPERIENCE: Icons.Icon.USER_EXPERIENCE,
  ICON_INVENTORY: Icons.Icon.INVENTORY,
  ICON_VENDOR: Icons.Icon.VENDOR,
  ICON_CUSTOMER_SERVICE: Icons.Icon.CUSTOMER_SERVICE,
  ICON_CONTENT_CREATION: Icons.Icon.CONTENT_CREATION,
  ICON_INVESTMENT: Icons.Icon.INVESTMENT,
  ICON_CONSULTING: Icons.Icon.CONSULTING,

  ICON_PENCIL_SQUARE: Icons.Icon.PENCIL_SQUARE,

  // Persona / general-purpose icons
  ICON_HIERARCHY: Icons.Icon.HIERARCHY,
  ICON_HEART: Icons.Icon.HEART,
  ICON_CROWN: Icons.Icon.CROWN,
  ICON_LIFELINE: Icons.Icon.LIFELINE,
  ICON_COIN: Icons.Icon.COIN,
  ICON_CURRENCY: Icons.Icon.CURRENCY,
  ICON_HEADSET: Icons.Icon.HEADSET,
  ICON_LINK: Icons.Icon.LINK,
  ICON_BOOKS: Icons.Icon.BOOKS,
};

function parseIconStringToEnum(icon: string): Icons.Icon | undefined {
  const trimmed = icon.trim();
  if (!trimmed) {
    return undefined;
  }
  const fromPrefixed = STRING_TO_ICON_MAPPING[trimmed];
  if (fromPrefixed !== undefined) {
    return fromPrefixed;
  }
  const fromShort = Icons.Icon[trimmed as keyof typeof Icons.Icon];
  if (typeof fromShort === "number") {
    return fromShort;
  }
  return undefined;
}

/**
 * Parse API / UI icon strings into `common.v3.Icon`: accepts `ICON_*` protobuf names and short enum names (e.g. `NEGOTIATION`).
 */
export function parseIconEnumFromString(icon?: string | null): Icons.Icon | undefined {
  if (icon == null) {
    return undefined;
  }
  return parseIconStringToEnum(icon);
}

/**
 * Normalize icon input to Icon enum value, handling both string and enum inputs
 */
function normalizeIcon(icon?: Icons.Icon | string | number): Icons.Icon | undefined {
  if (!icon) {
    return undefined;
  }

  // If it's already a number (Icon enum), return it
  if (typeof icon === "number") {
    return icon;
  }

  // If it's a string, try to map it to Icon enum
  if (typeof icon === "string") {
    return parseIconStringToEnum(icon);
  }

  return icon;
}

/**
 * Get the appropriate icon component for a given Icon enum value or string
 * @param icon - Icon enum value, string icon name, or undefined
 * @returns IconComponent to render
 */
export function getIconComponent(icon?: Icons.Icon | string | number): IconComponent {
  const normalizedIcon = normalizeIcon(icon);

  if (!normalizedIcon) {
    return QuestionMarkIcon;
  }

  return ICON_MAPPING[normalizedIcon] || QuestionMarkIcon;
}
